import React, {
  FunctionComponent,
  SVGProps,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import ClearIcon from "@mui/icons-material/Clear";
import cn from "classnames";
import { DownArrowIcon, DropdowSnIcon } from "Assets";
import Button from "Components/UI/Button/Button";
import { changeToParams } from "Shared/Utils/search-params";
import Icon from "Components/UI/Icon/Icon";

import style from "./ModalFiltersStaticOptions.module.scss";

type ModalFilterProps = {
  option: {
    label: string;
    value: string;
    img?: FunctionComponent<SVGProps<SVGSVGElement>>;
  }[];
  filterName: string;
  title?: string;
  isClearable?: boolean;
  onOptionChange?: (searchParams: URLSearchParams, value: string) => void;
};

const ModalFiltersStaticOptions: React.FC<ModalFilterProps> = ({
  option,
  filterName,
  isClearable = true,
  title,
  onOptionChange,
}) => {
  const [currentText, setCurrentText] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState(false);
  const [displayTitle, setDisplayTitle] = useState(title);
  const [tempSelectedValue, setTempSelectedValue] = useState<string>("");
  const [tempSelectedLabel, setTempSelectedLabel] = useState("");
  const modalRef = useRef<HTMLDivElement>(null);

  const toggleModal = () => setOpenModal(!openModal);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setOpenModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    const valueFromParams = searchParams.get(filterName) || "";
    const selectedOption = option.find((el) => el.value === valueFromParams);
    setDisplayTitle(selectedOption ? selectedOption.label : title);

    setTempSelectedValue(valueFromParams);
    setTempSelectedLabel(selectedOption ? selectedOption.label : title || "");
  }, [searchParams, filterName, option]);

  const onSearchChange = () => {
    onOptionChange?.(searchParams, tempSelectedValue);
    setSearchParams(
      changeToParams(filterName, tempSelectedValue, searchParams)
    );
    setOpenModal(false);
  };

  const handleSelectType = (value: string, label: string) => {
    setTempSelectedValue(value);
    setTempSelectedLabel(label);
    setCurrentText("");
  };

  const clearInput = () => {
    setCurrentText("");
    setTempSelectedValue("");
    setTempSelectedLabel("");
    searchParams.delete(filterName);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(filterName);
    setSearchParams(newSearchParams);
    setSearchParams(searchParams);
  };

  return (
    <div className={style.blockFiltering}>
      <div
        className={cn(
          style.blockFiltering__blockText,
          searchParams.has(filterName)
            ? style.blockFiltering__blockText_selected
            : ""
        )}
        onClick={toggleModal}
      >
        <span className={style.blockFiltering__blockText_itemTextSearch}>
          {displayTitle}
        </span>

        <DropdowSnIcon className={cn(style.icon, style.iconDefault)} />
        <DownArrowIcon className={cn(style.icon, style.iconLight)} />
      </div>

      {openModal && (
        <div ref={modalRef} className={style.blockFiltering__trueModal}>
          <div className={style.blockFiltering__trueModal_inModal}>
            <div
              className={style.blockFiltering__trueModal_inModal_paddingModal}
            >
              <input
                disabled={tempSelectedLabel.trim() !== ""}
                className={
                  style.blockFiltering__trueModal_inModal_paddingModal_inputClasses
                }
                placeholder="Select Item(s)"
                value={currentText || tempSelectedLabel}
              />
              {isClearable && (
                <ClearIcon
                  onClick={clearInput}
                  className={
                    style.blockFiltering__trueModal_inModal_paddingModal_clearIcon
                  }
                />
              )}

              {option.map((el) => (
                <div
                  key={el.value}
                  onClick={() => handleSelectType(el.value, el.label)}
                  className={cn(
                    style.blockFiltering__trueModal_inModal_paddingModal_flexElement,
                    tempSelectedValue === el.value ? style.activeItem : ""
                  )}
                >
                  {el.img && <Icon component={el.img} />}
                  <p
                    className={cn(
                      style.blockFiltering__trueModal_inModal_paddingModal_listItem
                    )}
                  >
                    {el.label}
                  </p>
                </div>
              ))}
            </div>
            <div className={style.blockFiltering__trueModal_inModal_lineBox}>
              <Button onClick={toggleModal}>Cancel</Button>
              <Button onClick={onSearchChange}>Apply</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModalFiltersStaticOptions;
